/**
 * useValidatedFormData Hook
 * Wraps useBRRFormData and merges validated state from Document Validation page.
 * Validated values take precedence over API data to ensure user edits persist across navigation.
 */

import { useMemo } from 'react';
import { BRRApiClient } from '../services';
import { BRRFormDataResponse } from '../services/types';
import { useBRRFormData } from './useBRRFormData';
import { useValidatedState } from '../contexts/ValidatedStateContext';

interface UseValidatedFormDataOptions {
  inspectionLotId: string | null;
  productName?: string;
  sessionId?: string | null;
  selectedLotIds?: string[];
  apiClient: BRRApiClient;
  autoLoad?: boolean;
}

interface UseValidatedFormDataReturn {
  data: BRRFormDataResponse | null;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

/**
 * Hook for loading BRR form data with validated state merged in.
 *
 * This hook:
 * 1. Loads raw data from API via useBRRFormData
 * 2. Loads validated state from the session store via useValidatedState
 * 3. Merges validated values into API response (validated takes precedence)
 *
 * Field path convention: "tableKey.extractionKey" maps to form data structure
 * Example: "COC.product_name" → brrData.productMarketData.productName
 *
 * @example
 * const { data, loading, error } = useValidatedFormData({
 *   inspectionLotId: '100000001',
 *   productName: 'Paracetamol 500mg',
 *   sessionId: 'abc-123',
 *   apiClient: brrApi,
 *   autoLoad: true
 * });
 */
export const useValidatedFormData = ({
  inspectionLotId,
  productName,
  sessionId,
  selectedLotIds,
  apiClient,
  autoLoad = true
}: UseValidatedFormDataOptions): UseValidatedFormDataReturn => {
  // Load raw API data with session scoping
  const { data: apiData, loading, error, reload } = useBRRFormData({
    inspectionLotId,
    productName,
    sessionId,
    selectedLotIds,
    apiClient,
    autoLoad
  });

  // Access validated state context
  const { validatedState } = useValidatedState();

  // Merge validated state into API data
  const mergedData = useMemo(() => {
    // If no API data yet, return null
    if (!apiData) {
      return null;
    }

    // If no validated state, return API data as-is
    if (!validatedState || !validatedState.fields || Object.keys(validatedState.fields).length === 0) {
      return apiData;
    }

    // Deep clone API data to avoid mutations
    const merged = JSON.parse(JSON.stringify(apiData)) as BRRFormDataResponse;

    // Apply validated field values
    // Field path format: "tableKey.extractionKey"
    // Examples:
    // - "COC.destination_country" → merged.productMarketData.targetCountry
    // - "DELIVERY_NOTE.quantity" → merged.sapIdentityBatch.batchQuantity
    Object.entries(validatedState.fields).forEach(([fieldPath, fieldValue]) => {
      const [tableKey, extractionKey] = fieldPath.split('.');

      // Map document validation table keys to form data structure
      // This is a simplified mapping - in production, you'd need a complete mapping table
      // For now, we'll apply to any matching field in the structure

      // Try to find and update the field in the merged data structure
      applyValidatedField(merged, tableKey, extractionKey, fieldValue);
    });

    return merged;
  }, [apiData, validatedState]);

  return {
    data: mergedData,
    loading,
    error,
    reload
  };
};

/**
 * Apply a validated field value to the merged form data structure.
 * Maps document validation table keys to form data sections.
 */
function applyValidatedField(
  formData: BRRFormDataResponse,
  tableKey: string,
  extractionKey: string,
  fieldValue: import('../services/types').FieldValue
): void {
  // Field mappings from DocumentValidation extraction keys to FormPage PRIMARY fields
  // IMPORTANT: Only map to primary fields (e.g., productName), NOT source fields (e.g., productNameSAP)
  // This ensures user edits appear in "BRR Field Value" column while "Source of Truth" remains unchanged
  //
  // NOTE: Multiple tables (COC, MAL, DELIVERY_NOTE) may have the same extraction key (e.g., destination_country).
  // We prioritize user overrides: if a field already has an override, don't let non-override values overwrite it.
  const fieldMappings: Record<string, { section: string; field: string }> = {
    // Product identification fields
    'product_name': { section: 'productMarketData', field: 'productName' },
    'destination_country': { section: 'productMarketData', field: 'targetCountry' },
    'dosage_form': { section: 'productMarketData', field: 'dosageForm' },
    'strength': { section: 'productMarketData', field: 'doseStrength' },
    'package_size_and_type': { section: 'productMarketData', field: 'packSize' },
    'sap_material_number': { section: 'productMarketData', field: 'materialSKU' },

    // Batch identification fields
    'quantity': { section: 'sapIdentityBatch', field: 'batchQuantity' },
    'fp_batch_quantity': { section: 'sapIdentityBatch', field: 'batchQuantity' },
    'manufacturing_date': { section: 'sapIdentityBatch', field: 'batchManufactureDate' },
    'expiry_date': { section: 'sapIdentityBatch', field: 'batchExpiryDate' },
    'cmo_fp_batch_id': { section: 'sapIdentityBatch', field: 'cmoFpBatchId' },
    'cmo_bulk_batch_id': { section: 'sapIdentityBatch', field: 'cmoBulkBatchId' },

    // Approval details
    'marketing_authorisation_number': { section: 'approvalDetails', field: 'marketAuthorisationNumber' },

    // Delivery note specific
    'destination_address': { section: 'productMarketData', field: 'marketCountryName' },

    // Transport report specific
    'storage_location': { section: 'sapIdentityBatch', field: 'targetStorageLocation' },
  };

  const mapping = fieldMappings[extractionKey];
  if (mapping) {
    // Use type assertion to access dynamic property
    const sectionKey = mapping.section as keyof BRRFormDataResponse;
    const section = formData[sectionKey];

    if (section && typeof section === 'object') {
      // Check if target field already has a user override
      const currentValue = (section as any)[mapping.field];

      // Don't let non-override values overwrite user edits
      // This handles the case where multiple tables (COC, MAL, DELIVERY_NOTE) have the same field
      if (currentValue?.override && !fieldValue.override) {
        return;
      }

      // Update the field with validated value and source metadata
      (section as any)[mapping.field] = fieldValue;
    }
  }

  // If no explicit mapping found, log for debugging
  // In production, you may want to expand the mapping table or use a more dynamic approach
}
